/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { defaultListingPageSize } from "Constants/AdminConstants";
import { OperationStatus } from "Constants/OperationStatus";

import {
  DistributionListsActionTypes,
  IAddMicrosoftMemberToDLRequest,
  IAddMicrosoftMemberToDLRequestFail,
  IAddMicrosoftMemberToDLRequestSuccess,
  IApproveOrDeclineJoinRequest,
  IApproveOrDeclineJoinRequestFail,
  IFetchDistributionListByTenantRequest,
  IFetchDistributionListByTenantRequestFail,
  IFetchDistributionListByTenantRequestSuccess,
  IFetchMicrosoftMembersForDLRequest,
  IFetchMicrosoftMembersForDLRequestFail,
  IFetchMicrosoftMembersForDLRequestSuccess,
  IRemoveMicrosoftMemberFromDLRequest,
  IRemoveMicrosoftMemberFromDLRequestFail,
  IRemoveMicrosoftMemberFromDLRequestSuccess,
  ISearchDistributionLists,
  ISearchDistributionListsFail,
  ISearchDistributionListsSuccess,
  IUpdateCurrentPage,
  IUpdatePageSize,
  IUpdateSelectedDistributionList,
} from "./DistributionListsAction";
import {
  ADD_MICROSOFT_MEMBER_TO_DL,
  ADD_MICROSOFT_MEMBER_TO_DL_FAIL,
  ADD_MICROSOFT_MEMBER_TO_DL_SUCCESS,
  APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST,
  APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST_FAIL,
  APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST_SUCCESS,
  DISTRIBUTION_LISTS_RESET,
  DISTRIBUTION_LISTS_SEARCH,
  DISTRIBUTION_LISTS_SEARCH_FAIL,
  DISTRIBUTION_LISTS_SEARCH_SUCCESS,
  FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST,
  FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST_FAIL,
  FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST_SUCCESS,
  FETCH_MICROSOFT_MEMBERS_FOR_DL,
  FETCH_MICROSOFT_MEMBERS_FOR_DL_FAIL,
  FETCH_MICROSOFT_MEMBERS_FOR_DL_SUCCESS,
  MICROSOFT_MEMBER_DL_RESET,
  REMOVE_MICROSOFT_MEMBER_FROM_DL,
  REMOVE_MICROSOFT_MEMBER_FROM_DL_FAIL,
  REMOVE_MICROSOFT_MEMBER_FROM_DL_SUCCESS,
  UPDATE_DISTRIBUTION_LISTS_LISTING_CURRENT_PAGE,
  UPDATE_DISTRIBUTION_LISTS_LISTING_PAGE_SIZE,
  UPDATE_SELECTED_DISTRIBUTION_LIST,
} from "./DistributionListsActionTypes";
import { IDistributionListsState } from "./IDistributionListsState";

const INITIAL_STATE: IDistributionListsState = {
  distributionLists: [],
  distributionListsByTenant: [],
  networkOperationStatusFetchByTenant: OperationStatus.Idle,
  fetchDistributionListsByTenantError: "",
  distributionListsByTenantCount: 0,
  networkOperationStatus: OperationStatus.Idle,
  networkOperationStatusApprove: OperationStatus.Idle,
  networkOperationStatusAddMember: OperationStatus.Idle,
  networkOperationStatusRemoveMember: OperationStatus.Idle,
  networkOperationStatusFetchMicrosoftMembers: OperationStatus.Idle,
  networkOperationError: "",
  networkOperationApproveError: "",
  currentPage: 1,
  pageSize: defaultListingPageSize,
  filteredCount: 0,
  selectedDistributionList: {},
  selectedDistributionListMicrosoftMembers: [],
  fetchMicrosoftMembersError: "",
  addMemberToDLErrorMessage: "",
  removeMemberFromDLErrorMessage: "",
};

const applySearchDistributionLists = (
  state: IDistributionListsState,
  action: ISearchDistributionLists
): IDistributionListsState => ({
  ...state,
  distributionLists: [],
  networkOperationStatus: OperationStatus.Pending,
  networkOperationError: "",
  currentPage: action.searchPage,
  currentTenant: action.tenant,
});

const applySearchDistributionListsSuccess = (
  state: IDistributionListsState,
  action: ISearchDistributionListsSuccess
): IDistributionListsState => ({
  ...state,
  distributionLists: action.distributionLists,
  currentPage: action.searchPage,
  filteredCount: action.filteredCount,
  networkOperationStatus: OperationStatus.Succeeded,
  networkOperationError: "",
});

const applySearchDistributionListsFail = (
  state: IDistributionListsState,
  action: ISearchDistributionListsFail
): IDistributionListsState => ({
  ...state,
  networkOperationStatus: OperationStatus.Failed,
  networkOperationError: action.error,
});

const applyResetDistributionLists = (): IDistributionListsState => INITIAL_STATE;

const applyUpdateCurrentPage = (
  state: IDistributionListsState,
  action: IUpdateCurrentPage
): IDistributionListsState => ({
  ...state,
  currentPage: action.currentPage,
});

const applyUpdatePageSize = (
  state: IDistributionListsState,
  action: IUpdatePageSize
): IDistributionListsState => ({
  ...state,
  pageSize: action.pageSize,
});

const applyApproveOrDeclineJoinRequest = (
  state: IDistributionListsState,
  action: IApproveOrDeclineJoinRequest
): IDistributionListsState => ({
  ...state,
  networkOperationStatusApprove: OperationStatus.Pending,
  networkOperationApproveError: "",
});

const applyApproveOrDeclineJoinRequestSuccess = (
  state: IDistributionListsState
): IDistributionListsState => ({
  ...state,
  networkOperationStatusApprove: OperationStatus.Succeeded,
  networkOperationApproveError: "",
});

const applyApproveOrDeclineJoinRequestFail = (
  state: IDistributionListsState,
  action: IApproveOrDeclineJoinRequestFail
): IDistributionListsState => ({
  ...state,
  networkOperationStatusApprove: OperationStatus.Failed,
  networkOperationApproveError: action.error,
});

const applyFetchDistributionListsByTenant = (
  state: IDistributionListsState,
  action: IFetchDistributionListByTenantRequest
) => ({
  ...state,
  fetchDistributionListsByTenantError: "",
  distributionListsByTenant: [],
  networkOperationStatusFetchByTenant: OperationStatus.Pending,
});

const applyFetchDistributionListsByTenantSuccess = (
  state: IDistributionListsState,
  action: IFetchDistributionListByTenantRequestSuccess
): IDistributionListsState => ({
  ...state,
  distributionListsByTenant: action.distributionListsByTenant,
  networkOperationStatusFetchByTenant: OperationStatus.Succeeded,
  distributionListsByTenantCount: action.count,
});

const applyFetchDistributionListsByTenantFail = (
  state: IDistributionListsState,
  action: IFetchDistributionListByTenantRequestFail
): IDistributionListsState => ({
  ...state,
  networkOperationStatusFetchByTenant: OperationStatus.Failed,
  networkOperationApproveError: action.errorMessage,
  fetchDistributionListsByTenantError: action.errorMessage,
});

const applyUpdateSelectedDistributionList = (
  state: IDistributionListsState,
  action: IUpdateSelectedDistributionList
): IDistributionListsState => ({
  ...state,
  selectedDistributionList: state.distributionListsByTenant.filter(
    (dl) => dl.distributionListEmail === action.distributionListEmail
  )[0],
});

const applyFetchMicrosoftMembersForDL = (
  state: IDistributionListsState,
  action: IFetchMicrosoftMembersForDLRequest
): IDistributionListsState => ({
  ...state,
  selectedDistributionListMicrosoftMembers: [],
  fetchMicrosoftMembersError: "",
  networkOperationStatusFetchMicrosoftMembers: OperationStatus.Pending,
  networkOperationStatusAddMember: OperationStatus.Idle,
  networkOperationStatusRemoveMember: OperationStatus.Idle,
});

const applyFetchMicrosoftMembersForDLFail = (
  state: IDistributionListsState,
  action: IFetchMicrosoftMembersForDLRequestFail
): IDistributionListsState => ({
  ...state,
  fetchMicrosoftMembersError: action.errorMessage,
  networkOperationStatusFetchMicrosoftMembers: OperationStatus.Failed,
});

const applyFetchMicrosoftMembersForDLSuccess = (
  state: IDistributionListsState,
  action: IFetchMicrosoftMembersForDLRequestSuccess
): IDistributionListsState => ({
  ...state,
  selectedDistributionListMicrosoftMembers: action.microsoftMembers,
  networkOperationStatusFetchMicrosoftMembers: OperationStatus.Succeeded,
});

const applyAddMicrosoftMemberToDL = (
  state: IDistributionListsState,
  action: IAddMicrosoftMemberToDLRequest
): IDistributionListsState => ({
  ...state,
  networkOperationStatusAddMember: OperationStatus.Pending,
  networkOperationStatusRemoveMember: OperationStatus.Idle,
});

const applyAddMicrosoftMemberToDLFail = (
  state: IDistributionListsState,
  action: IAddMicrosoftMemberToDLRequestFail
): IDistributionListsState => ({
  ...state,
  networkOperationStatusAddMember: OperationStatus.Failed,
  addMemberToDLErrorMessage: action.errorMessage
    ? action.errorMessage
    : "Failed to add this user to the distribution list.",
});

const applyAddMicrosoftMemberToDLSuccess = (
  state: IDistributionListsState,
  action: IAddMicrosoftMemberToDLRequestSuccess
): IDistributionListsState => ({
  ...state,
  networkOperationStatusAddMember: OperationStatus.Succeeded,
});

const applyResetMicrosoftMemberDLs = (state: IDistributionListsState): IDistributionListsState => ({
  ...state,
  networkOperationStatusAddMember: OperationStatus.Idle,
  networkOperationStatusRemoveMember: OperationStatus.Idle,
  addMemberToDLErrorMessage: "",
  removeMemberFromDLErrorMessage: "",
});

const applyRemoveMicrosoftMemberFromDL = (
  state: IDistributionListsState,
  action: IRemoveMicrosoftMemberFromDLRequest
): IDistributionListsState => ({
  ...state,
  networkOperationStatusRemoveMember: OperationStatus.Pending,
  networkOperationStatusAddMember: OperationStatus.Idle,
});

const applyRemoveMicrosoftMemberFromDLFail = (
  state: IDistributionListsState,
  action: IRemoveMicrosoftMemberFromDLRequestFail
): IDistributionListsState => ({
  ...state,
  networkOperationStatusRemoveMember: OperationStatus.Failed,
  removeMemberFromDLErrorMessage: action.errorMessage
    ? action.errorMessage
    : "Failed to remove this user from the distribution list.",
});

const applyRemoveMicrosoftMemberFromDLSuccess = (
  state: IDistributionListsState,
  action: IRemoveMicrosoftMemberFromDLRequestSuccess
): IDistributionListsState => ({
  ...state,
  networkOperationStatusRemoveMember: OperationStatus.Succeeded,
});

function distributionListsReducer(
  state = INITIAL_STATE,
  action: DistributionListsActionTypes
): IDistributionListsState {
  switch (action.type) {
    case ADD_MICROSOFT_MEMBER_TO_DL: {
      return applyAddMicrosoftMemberToDL(state, action);
    }
    case ADD_MICROSOFT_MEMBER_TO_DL_FAIL: {
      return applyAddMicrosoftMemberToDLFail(state, action);
    }
    case ADD_MICROSOFT_MEMBER_TO_DL_SUCCESS: {
      return applyAddMicrosoftMemberToDLSuccess(state, action);
    }
    case MICROSOFT_MEMBER_DL_RESET: {
      return applyResetMicrosoftMemberDLs(state);
    }
    case DISTRIBUTION_LISTS_SEARCH: {
      return applySearchDistributionLists(state, action);
    }
    case DISTRIBUTION_LISTS_SEARCH_FAIL: {
      return applySearchDistributionListsFail(state, action);
    }
    case DISTRIBUTION_LISTS_SEARCH_SUCCESS: {
      return applySearchDistributionListsSuccess(state, action);
    }
    case DISTRIBUTION_LISTS_RESET: {
      return applyResetDistributionLists();
    }
    case REMOVE_MICROSOFT_MEMBER_FROM_DL: {
      return applyRemoveMicrosoftMemberFromDL(state, action);
    }
    case REMOVE_MICROSOFT_MEMBER_FROM_DL_FAIL: {
      return applyRemoveMicrosoftMemberFromDLFail(state, action);
    }
    case REMOVE_MICROSOFT_MEMBER_FROM_DL_SUCCESS: {
      return applyRemoveMicrosoftMemberFromDLSuccess(state, action);
    }
    case UPDATE_DISTRIBUTION_LISTS_LISTING_CURRENT_PAGE: {
      return applyUpdateCurrentPage(state, action);
    }
    case UPDATE_DISTRIBUTION_LISTS_LISTING_PAGE_SIZE: {
      return applyUpdatePageSize(state, action);
    }
    case APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST: {
      return applyApproveOrDeclineJoinRequest(state, action);
    }
    case APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST_SUCCESS: {
      return applyApproveOrDeclineJoinRequestSuccess(state);
    }
    case APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST_FAIL: {
      return applyApproveOrDeclineJoinRequestFail(state, action);
    }
    case FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST: {
      return applyFetchDistributionListsByTenant(state, action);
    }
    case FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST_SUCCESS: {
      return applyFetchDistributionListsByTenantSuccess(state, action);
    }
    case FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST_FAIL: {
      return applyFetchDistributionListsByTenantFail(state, action);
    }
    case UPDATE_SELECTED_DISTRIBUTION_LIST: {
      return applyUpdateSelectedDistributionList(state, action);
    }
    case FETCH_MICROSOFT_MEMBERS_FOR_DL: {
      return applyFetchMicrosoftMembersForDL(state, action);
    }
    case FETCH_MICROSOFT_MEMBERS_FOR_DL_SUCCESS: {
      return applyFetchMicrosoftMembersForDLSuccess(state, action);
    }
    case FETCH_MICROSOFT_MEMBERS_FOR_DL_FAIL: {
      return applyFetchMicrosoftMembersForDLFail(state, action);
    }
    default:
      return state;
  }
}

export default distributionListsReducer;
